// features/findings/charts/predictions-block.js
// Predictions block — structured display of pre-registered predictions
// with resolution dates, central estimate, falsifier band, and status.

export function PredictionsBlock({ title, predictions }) {
  if (!predictions || predictions.length === 0) return null;

  return (
    <figure className="finding-chart finding-pred-block">
      <figcaption className="finding-chart-caption">{title}</figcaption>
      <div className="finding-pred-list">
        {predictions.map((p, i) => (
          <PredictionCard key={i} num={i + 1} prediction={p} />
        ))}
      </div>
    </figure>
  );
}

function PredictionCard({ num, prediction }) {
  const {
    label,
    band,             // [low, high]
    central,          // number
    falsifierBand,    // [outerLow, outerHigh]
    resolutionDate,   // 'YYYY-MM' or 'YYYY'
    status = 'open',  // 'open' | 'resolved-correct' | 'resolved-incorrect' | 'resolved-partial'
    note,
  } = prediction;

  const statusTone =
    status === 'resolved-correct' ? 'success' :
    status === 'resolved-incorrect' ? 'failure' :
    status === 'resolved-partial' ? 'warn' :
    'neutral';

  return (
    <div className="finding-pred-card">
      <div className="finding-pred-head">
        <div className="finding-pred-num">Prediction {num}</div>
        <div className={`finding-pill finding-pill-${statusTone}`}>{status}</div>
      </div>
      <div className="finding-pred-label">{label}</div>

      {(band || falsifierBand) && (
        <div className="finding-pred-band-row">
          <PredictionBand band={band} central={central} falsifierBand={falsifierBand} />
        </div>
      )}

      <dl className="finding-pred-meta">
        {band && (
          <>
            <dt>Predicted band</dt>
            <dd><code>[{band[0]}, {band[1]}]</code>{central != null && <> · central <code>{central}</code></>}</dd>
          </>
        )}
        {falsifierBand && (
          <>
            <dt>Falsifier outside</dt>
            <dd><code>[{falsifierBand[0]}, {falsifierBand[1]}]</code></dd>
          </>
        )}
        {resolutionDate && (
          <>
            <dt>Resolution</dt>
            <dd>{resolutionDate}</dd>
          </>
        )}
      </dl>
      {note && <div className="finding-pred-note">{note}</div>}
    </div>
  );
}

function PredictionBand({ band, central, falsifierBand }) {
  if (!band) return null;
  const outerLow = falsifierBand?.[0] ?? band[0];
  const outerHigh = falsifierBand?.[1] ?? band[1];
  const range = outerHigh - outerLow;
  if (range <= 0) return null;
  const pct = (v) => ((v - outerLow) / range) * 100;

  return (
    <div className="finding-pred-band">
      <div className="finding-pred-band-track">
        {falsifierBand && (
          <div
            className="finding-pred-band-falsifier"
            style={{ left: `${pct(falsifierBand[0])}%`, width: `${pct(falsifierBand[1]) - pct(falsifierBand[0])}%` }}
          />
        )}
        <div
          className="finding-pred-band-predicted"
          style={{ left: `${pct(band[0])}%`, width: `${pct(band[1]) - pct(band[0])}%` }}
        />
        {central != null && (
          <div
            className="finding-pred-band-central"
            style={{ left: `${pct(central)}%` }}
          />
        )}
      </div>
      <div className="finding-pred-band-axis">
        <span>{outerLow}</span>
        <span>{outerHigh}</span>
      </div>
    </div>
  );
}

export default PredictionsBlock;
